import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Thermometer, Gauge, Activity, Clock, ChevronDown, ChevronUp, Cable, Wifi, WifiOff } from 'lucide-react';
import type { InverterCurrent, InverterString } from '@/api/inverterDetail';
import { useI18n } from '@/i18n';

type Props = {
  current: InverterCurrent;
  strings: InverterString[];
};

function ConnBadge({ active, label }: { active: boolean; label: string }) {
  const { t } = useI18n();
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
    }`}>
      {active ? <Wifi size={11} /> : <WifiOff size={11} />}
      {label === 'FTP' ? t('common.ftp') : t('common.modbus')}
    </span>
  );
}

function DataItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-lg border bg-white p-2">
      <div className="flex items-center gap-1 text-xs text-gray-500">{icon}<span>{label}</span></div>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

export default function InverterDetailCard({ current: inv, strings }: Props) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [showElectrical, setShowElectrical] = useState(false);
  const [showStrings, setShowStrings] = useState(false);

  const isOnline = inv.inverter_status === 1;
  const val = (v: number | string | null | undefined) =>
    isOnline && v != null ? String(v) : '--';

  return (
    <div className="space-y-4 rounded-2xl bg-white p-4 shadow-md">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{inv.inverter_brand} {inv.inverter_model}</p>
          <p className="text-xs text-gray-400">{t('common.serialNumber')}: {inv.inverter_serial_number}</p>
        </div>
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
          isOnline ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
        }`}>
          {isOnline ? t('common.online') : t('common.offline')}
        </span>
      </div>

      {/* FTP / Modbus bağlantı durumu */}
      <div className="flex gap-2">
        <ConnBadge active={inv.inverter_ftp_connection_status === 1} label="FTP" />
        <ConnBadge active={inv.inverter_modbus_connection_status === 1} label="Modbus" />
      </div>

      {/* Güç özeti */}
      <div className="rounded-lg bg-gray-50 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Zap size={16} className="text-emerald-600" />
            <span>{t('inverterDetail.activePower')}</span>
          </div>
          <span className="text-2xl font-bold">
            {isOnline && inv.inverter_active_power != null ? `${inv.inverter_active_power} kW` : '--'}
          </span>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          <strong>{t('inverterDetail.daily')}:</strong> {inv.inverter_e_day ?? '--'}
          {' · '}
          <strong>{t('inverterDetail.total')}:</strong> {inv.inverter_e_total ?? '--'}
        </p>
      </div>

      {/* Elektriksel veriler */}
      <button
        onClick={() => setShowElectrical(p => !p)}
        className="flex w-full items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold hover:bg-gray-200"
      >
        <span>{t('inverterDetail.electrical')}</span>
        {showElectrical ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showElectrical && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <DataItem icon={<Gauge size={13} />}    label={t('inverterDetail.voltageLineAB')}  value={val(inv.inverter_voltage_line_ab) !== '--' ? `${val(inv.inverter_voltage_line_ab)} V` : '--'} />
          <DataItem icon={<Gauge size={13} />}    label={t('inverterDetail.voltageLineBC')}  value={val(inv.inverter_voltage_line_bc) !== '--' ? `${val(inv.inverter_voltage_line_bc)} V` : '--'} />
          <DataItem icon={<Gauge size={13} />}    label={t('inverterDetail.voltageLineCA')}  value={val(inv.inverter_voltage_line_ca) !== '--' ? `${val(inv.inverter_voltage_line_ca)} V` : '--'} />
          <DataItem icon={<Activity size={13} />} label={t('inverterDetail.currentPhaseA')}  value={val(inv.inverter_current_phase_a) !== '--' ? `${val(inv.inverter_current_phase_a)} A` : '--'} />
          <DataItem icon={<Activity size={13} />} label={t('inverterDetail.currentPhaseB')}  value={val(inv.inverter_current_phase_b) !== '--' ? `${val(inv.inverter_current_phase_b)} A` : '--'} />
          <DataItem icon={<Activity size={13} />} label={t('inverterDetail.currentPhaseC')}  value={val(inv.inverter_current_phase_c) !== '--' ? `${val(inv.inverter_current_phase_c)} A` : '--'} />
          <DataItem icon={<Zap size={13} />}      label={t('inverterDetail.powerFactor')}    value={val(inv.inverter_power_factor)} />
          <DataItem icon={<Zap size={13} />}      label={t('inverterDetail.reactivePower')}  value={val(inv.inverter_reactive_power) !== '--' ? `${val(inv.inverter_reactive_power)} kVAR` : '--'} />
          <DataItem icon={<Zap size={13} />}      label={t('inverterDetail.frequency')}      value={val(inv.inverter_grid_frequency) !== '--' ? `${val(inv.inverter_grid_frequency)} Hz` : '--'} />
          <DataItem icon={<Thermometer size={13} />} label={t('inverterDetail.temperature')} value={val(inv.inverter_internal_temperature) !== '--' ? `${val(inv.inverter_internal_temperature)} °C` : '--'} />
          <DataItem icon={<Cable size={13} />}    label={t('inverterDetail.stringCount')}    value={inv.inverter_string_count != null ? String(inv.inverter_string_count) : '--'} />
          <DataItem icon={<Cable size={13} />}    label={t('inverterDetail.capacity')}       value={inv.inverter_capacity_kwp != null ? `${inv.inverter_capacity_kwp} kWp` : '--'} />
        </div>
      )}

      {/* String verileri */}
      <button
        onClick={() => setShowStrings(p => !p)}
        className="flex w-full items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold hover:bg-gray-200"
      >
        <span>{t('inverterDetail.strings')}</span>
        {showStrings ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {showStrings && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {strings.map((s) => {
            const current = s.string_current_ftp ?? s.string_current_modbus;
            const voltage = s.string_voltage_ftp ?? s.string_voltage_modbus;
            const isActive = (current ?? 0) > 0;
            const effPct = s.string_efficiency != null ? Math.round(s.string_efficiency * 100) : null;
            const effColor = effPct == null ? 'text-gray-400'
              : effPct >= 80 ? 'text-emerald-600'
              : effPct >= 50 ? 'text-yellow-600'
              : 'text-rose-600';
            return (
              <div
                key={s.string_number}
                onClick={() => navigate(`/dashboard/inverters/${inv.inverter_id}/string/${s.string_number}`)}
                className={`cursor-pointer rounded-lg p-3 text-center border transition-shadow hover:shadow-md ${
                  isActive ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="text-sm font-semibold text-gray-700">{t('inverterDetail.chart.stringPrefix')} {s.string_number}</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  {current != null ? `${current} A` : '--'} / {voltage != null ? `${voltage} V` : '--'}
                </div>
                <div className={`text-xs font-medium mt-0.5 ${effColor}`}>
                  {effPct != null ? `${effPct}%` : '--'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Son veri zamanı */}
      <div className="flex items-center justify-between text-xs text-gray-400">
        <div className="flex items-center gap-1"><Clock size={12} /><span>{t('inverterDetail.lastData')}</span></div>
        <span>{inv.inverter_last_seen ?? '--'}</span>
      </div>
    </div>
  );
}
